import React, { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import axios from "axios";

interface DataFlowEditProps {
  control: any;
  errors: any;
  watch: any;
  setValue: any;
  datum: any;
}

const DataFlowEdit: React.FC<DataFlowEditProps> = ({
  control,
  errors,
  watch,
  setValue,
  datum,
}) => {
  const [dataSystems, setDataSystems] = useState<any[]>([]);
  const [departmentFeedsOptions, setDepartmentFeedsOptions] = useState<any[]>(
    []
  );
  const [deptSystemOptions, setDeptSystemOptions] = useState<any[]>([]);
  const [loadingSystems, setLoadingSystems] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingDeptSystems, setLoadingDeptSystems] = useState(false);

  // Watch form values for dependent data fetching
  const deptsFeedDataSystemValue = watch("deptsFeedDataSystem");
  const deptReceiveDataValue = watch("deptReceiveData");

  // Fetch Data Systems & ERP modules based on deptsFeedDataSystem
  useEffect(() => {
    const fetchDataSystems = async () => {
      try {
        setLoadingSystems(true);
        setLoadingDepartments(true);

        let response;

        if (
          deptsFeedDataSystemValue &&
          deptsFeedDataSystemValue.length > 0 &&
          !deptsFeedDataSystemValue.includes("N/A")
        ) {
          response = await axios.post(
            "/api/v1/data-system/manage-department-system",
            {
              email: datum?.profile?.user?.email,
              user: datum?.profile?.user?._id,
              systemSelection: [
                { category: "departmentName", data: deptsFeedDataSystemValue },
              ],
            }
          );
        } else {
          response = await axios.get(
            "/api/v1/data-system/manage-department-system",
            {
              params: {
                pagination: "not",
                email: datum?.profile?.user?.email,
                user: datum?.profile?.user?._id,
                datasystem: datum?._id,
              },
            }
          );
        }

        const docs = response?.data?.data?.docs || [];

        const departmentNameCategory = docs.find(
          (item: any) => item.category === "departmentName"
        );
        setDataSystems(departmentNameCategory?.data || []);

        const erpModuleCategory = docs.find(
          (item: any) => item.category === "erpModule"
        );
        setDepartmentFeedsOptions(
          erpModuleCategory?.data?.map((d: any) => ({
            value: d.title,
            label: d.title,
          })) || []
        );
      } catch (error: any) {
        console.error(
          "Error fetching data systems:",
          error.response?.data || error.message
        );
      } finally {
        setLoadingSystems(false);
        setLoadingDepartments(false);
      }
    };

    if (datum) fetchDataSystems();
  }, [datum, deptsFeedDataSystemValue]);

  // Fetch Systems that deptReceiveData can send to
  useEffect(() => {
    const fetchDeptSystems = async () => {
      try {
        setLoadingDeptSystems(true);

        let response;
        if (
          deptReceiveDataValue &&
          deptReceiveDataValue.length > 0 &&
          !deptReceiveDataValue.includes("N/A")
        ) {
          response = await axios.post(
            "/api/v1/data-system/manage-department-system",
            {
              email: datum?.profile?.user?.email,
              user: datum?.profile?.user?._id,
              systemSelection: [
                { category: "departmentName", data: deptReceiveDataValue },
              ],
            }
          );

          const docs = response?.data?.data?.docs || [];
          const erpModuleCategory = docs.find(
            (item: any) => item.category === "erpModule"
          );

          setDeptSystemOptions(
            erpModuleCategory?.data?.map((d: any) => ({
              value: d.title,
              label: d.title,
            })) || []
          );
        } else {
          setDeptSystemOptions([]);
        }
      } catch (error: any) {
        console.error(
          "Error fetching dept systems:",
          error.response?.data || error.message
        );
      } finally {
        setLoadingDeptSystems(false);
      }
    };

    if (
      deptReceiveDataValue &&
      deptReceiveDataValue.length > 0 &&
      !deptReceiveDataValue.includes("N/A")
    ) {
      fetchDeptSystems();
    } else {
      setDeptSystemOptions([]);
    }
  }, [deptReceiveDataValue, datum]);

  const dataSystemsOptions = [
    { value: "N/A", label: "N/A" },
    ...dataSystems.map((item: any) => ({
      value: item.title,
      label: item.title,
    })),
  ];

  const departmentFeedsOptionsWithNA = [
    { value: "N/A", label: "N/A" },
    ...departmentFeedsOptions,
  ];

  const deptSystemOptionsWithNA = [
    { value: "N/A", label: "N/A" },
    ...deptSystemOptions,
  ];
  // Handler for select changes that handles N/A logic
  const handleSelectChange = (
    fieldName: string,
    selected: any,
    isMulti: boolean = true
  ) => {
    if (!selected) {
      setValue(fieldName, []);
      return;
    }

    if (isMulti) {
      const selectedValues = selected.map((opt: any) => opt.value);

      // If N/A is selected, clear all other selections and set to ["N/A"]
      if (selectedValues.includes("N/A")) {
        setValue(fieldName, ["N/A"]);
      }
      // If other options are selected and N/A was previously selected, remove N/A
      else if (selectedValues.length > 0) {
        setValue(
          fieldName,
          selectedValues.filter((val: string) => val !== "N/A")
        );
      }
      // If nothing is selected, set to empty array
      else {
        setValue(fieldName, []);
      }
    } else {
      // For single select (if needed in future)
      setValue(fieldName, selected?.value || "");
    }
  };

  // Get current value for display (handles N/A case)
  const getSelectValue = (fieldName: string, options: any[]) => {
    const fieldValue = watch(fieldName) || [];

    if (fieldValue.includes("N/A")) {
      return options.filter((opt) => opt.value === "N/A");
    }

    return options.filter((opt) => fieldValue.includes(opt.value));
  };

  return (
    <div className="font-Inter max-w-2xl">
      <div className="space-y-6">
        {/* Department feeds data to system */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Which department feeds data to this system{" "}
            <span className="text-red-500">*</span>
          </label>
          <Controller
            name="deptsFeedDataSystem"
            control={control}
            render={({ field }) => (
              <Select
                isMulti
                isLoading={loadingSystems}
                options={dataSystemsOptions}
                placeholder="Select department(s)..."
                onChange={(selected) =>
                  handleSelectChange("deptsFeedDataSystem", selected)
                }
                value={getSelectValue(
                  "deptsFeedDataSystem",
                  dataSystemsOptions
                )}
                closeMenuOnSelect={false}
              />
            )}
          />
          {errors.deptsFeedDataSystem && (
            <p className="text-red-500 text-xs mt-1">
              {errors.deptsFeedDataSystem.message}
            </p>
          )}
        </div>

        {/* System feeds data to this system */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Which system feeds data to this system{" "}
            <span className="text-red-500">*</span>
          </label>
          <Controller
            name="systemsFeedDataSystem"
            control={control}
            render={({ field }) => (
              <Select
                isMulti
                isLoading={loadingDepartments}
                options={departmentFeedsOptionsWithNA}
                placeholder="Select system(s)..."
                onChange={(selected) =>
                  handleSelectChange("systemsFeedDataSystem", selected)
                }
                value={getSelectValue(
                  "systemsFeedDataSystem",
                  departmentFeedsOptionsWithNA
                )}
                closeMenuOnSelect={false}
              />
            )}
          />
          {errors.systemsFeedDataSystem && (
            <p className="text-red-500 text-xs mt-1">
              {errors.systemsFeedDataSystem.message}
            </p>
          )}
        </div>

        {/* Department receives data */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Which department receives data from this system?
            <span className="text-red-500">*</span>
          </label>
          <Controller
            name="deptReceiveData"
            control={control}
            render={({ field }) => (
              <Select
                isMulti
                isLoading={loadingSystems}
                options={dataSystemsOptions}
                placeholder="Select department(s)..."
                onChange={(selected) =>
                  handleSelectChange("deptReceiveData", selected)
                }
                value={getSelectValue("deptReceiveData", dataSystemsOptions)}
                closeMenuOnSelect={false}
              />
            )}
          />
          {errors.deptReceiveData && (
            <p className="text-red-500 text-xs mt-1">
              {errors.deptReceiveData.message}
            </p>
          )}
        </div>

        {/* System receives data */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Which system receives data from this system?
            <span className="text-red-500">*</span>
          </label>
          <Controller
            name="deptSystemReceiveData"
            control={control}
            render={({ field }) => (
              <Select
                isMulti
                isLoading={loadingDeptSystems}
                options={deptSystemOptionsWithNA}
                placeholder="Select system(s)..."
                onChange={(selected) =>
                  handleSelectChange("deptSystemReceiveData", selected)
                }
                value={getSelectValue(
                  "deptSystemReceiveData",
                  deptSystemOptionsWithNA
                )}
                closeMenuOnSelect={false}
              />
            )}
          />
          {errors.deptSystemReceiveData && (
            <p className="text-red-500 text-xs mt-1">
              {errors.deptSystemReceiveData.message}
            </p>
          )}
        </div>

        {/* Reports produced */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            What reports are produced for decision-making?
            <span className="text-red-500">*</span>
          </label>
          <Controller
            name="reportProduced"
            control={control}
            render={({ field }) => (
              <textarea
                {...field}
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical ${
                  errors.reportProduced ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="E.g. Commercial-billing system-customers numbers"
              />
            )}
          />
          {errors.reportProduced && (
            <p className="text-red-500 text-xs mt-1">
              {errors.reportProduced.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataFlowEdit;
